import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Award, Users, Heart, TrendingUp, 
  CheckCircle, AlertCircle, Star, Medal, Trophy,
  ChevronRight, Save, Plus, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VolunteerEnhanced = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [volunteerId] = useState('volunteer_' + Math.random().toString(36).substr(2, 9));
  const [impact, setImpact] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [openShifts, setOpenShifts] = useState([]);
  const [training, setTraining] = useState(null);
  const [teamDashboard, setTeamDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [impactRes, availRes, shiftsRes, trainingRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/api/phase32/volunteer/${volunteerId}/impact`),
        fetch(`${API_URL}/api/phase32/volunteer/${volunteerId}/availability`),
        fetch(`${API_URL}/api/phase32/volunteer/${volunteerId}/shifts/open`),
        fetch(`${API_URL}/api/phase32/volunteer/${volunteerId}/training`),
        fetch(`${API_URL}/api/phase32/volunteer/team/dashboard`)
      ]);

      if (impactRes.ok) setImpact(await impactRes.json());
      if (availRes.ok) setAvailability(await availRes.json());
      if (shiftsRes.ok) {
        const data = await shiftsRes.json();
        setOpenShifts(data.shifts || []);
      }
      if (trainingRes.ok) setTraining(await trainingRes.json());
      if (teamRes.ok) setTeamDashboard(await teamRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleScheduleShift = async (shiftId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/phase32/volunteer/${volunteerId}/shifts/${shiftId}/schedule`,
        { method: 'POST' }
      );
      if (response.ok) {
        toast.success('Successfully scheduled for shift!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to schedule shift');
      }
    } catch (error) {
      toast.error('Error scheduling shift');
    }
  };

  const updateAvailability = async (day, field, value) => {
    const updatedSchedule = { ...availability?.weekly_schedule };
    updatedSchedule[day] = { ...updatedSchedule[day], [field]: value };
    
    try {
      const response = await fetch(
        `${API_URL}/api/phase32/volunteer/${volunteerId}/availability`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weekly_schedule: updatedSchedule })
        }
      );
      if (response.ok) {
        setAvailability(prev => ({ ...prev, weekly_schedule: updatedSchedule }));
        toast.success('Availability updated');
      }
    } catch (error) {
      toast.error('Error updating availability');
    }
  };

  const getBadgeIcon = (badgeId) => {
    if (badgeId.includes('hours')) return <Clock className="h-4 w-4" />;
    if (badgeId.includes('shifts')) return <CheckCircle className="h-4 w-4" />;
    if (badgeId.includes('patients')) return <Heart className="h-4 w-4" />;
    return <Star className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="volunteer-enhanced">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-7 w-7 text-teal-600" />
            Enhanced Volunteer Portal
          </h1>
          <p className="text-gray-600 mt-1">Self-scheduling, impact tracking, and training management</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Self-Schedule</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="impact" data-testid="tab-impact">My Impact</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">Training</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Hours</p>
                    <p className="text-2xl font-bold text-teal-600">{impact?.total_hours || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-teal-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Patients Served</p>
                    <p className="text-2xl font-bold text-blue-600">{impact?.patients_served || 0}</p>
                  </div>
                  <Heart className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Impact Score</p>
                    <p className="text-2xl font-bold text-purple-600">{impact?.impact_score || 0}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Badges Earned</p>
                    <p className="text-2xl font-bold text-amber-600">{impact?.badges?.length || 0}</p>
                  </div>
                  <Award className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Badges */}
          {impact?.badges?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Your Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {impact.badges.map((badge) => (
                    <div
                      key={badge.id}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full"
                    >
                      {getBadgeIcon(badge.id)}
                      <div>
                        <p className="font-medium text-sm">{badge.name}</p>
                        <p className="text-xs text-gray-500">{badge.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team Dashboard */}
          {teamDashboard && (
            <Card>
              <CardHeader>
                <CardTitle>Team Impact Dashboard</CardTitle>
                <CardDescription>Aggregate metrics for all volunteers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-teal-50 rounded-lg">
                    <p className="text-2xl font-bold text-teal-600">{teamDashboard.total_hours}</p>
                    <p className="text-sm text-gray-600">Total Hours</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{teamDashboard.total_shifts}</p>
                    <p className="text-sm text-gray-600">Total Shifts</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{teamDashboard.active_volunteers}</p>
                    <p className="text-sm text-gray-600">Active Volunteers</p>
                  </div>
                  <div className="text-center p-4 bg-pink-50 rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">{teamDashboard.patients_served}</p>
                    <p className="text-sm text-gray-600">Patients Served</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{teamDashboard.open_shifts}</p>
                    <p className="text-sm text-gray-600">Open Shifts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Self-Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-teal-600" />
                Available Shifts
              </CardTitle>
              <CardDescription>Sign up for shifts that match your availability</CardDescription>
            </CardHeader>
            <CardContent>
              {openShifts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No open shifts available at this time</p>
                  <p className="text-sm">Check back later or contact the volunteer coordinator</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {openShifts.map((shift) => (
                    <div
                      key={shift.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <Calendar className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium">{shift.activity_type}</p>
                          <p className="text-sm text-gray-500">
                            {shift.date} • {shift.start_time} - {shift.end_time}
                          </p>
                          <p className="text-sm text-gray-500">{shift.location}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {shift.matches_availability && (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            Matches Your Schedule
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleScheduleShift(shift.id)}
                          data-testid={`schedule-shift-${shift.id}`}
                        >
                          Sign Up
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-teal-600" />
                Weekly Availability
              </CardTitle>
              <CardDescription>Set your regular availability for volunteer shifts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                  <div key={day} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={availability?.weekly_schedule?.[day]?.available || false}
                        onCheckedChange={(checked) => updateAvailability(day, 'available', checked)}
                        data-testid={`availability-${day}`}
                      />
                      <span className="font-medium capitalize w-24">{day}</span>
                    </div>
                    {availability?.weekly_schedule?.[day]?.available && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          className="w-32"
                          value={availability?.weekly_schedule?.[day]?.start_time || '09:00'}
                          onChange={(e) => updateAvailability(day, 'start_time', e.target.value)}
                        />
                        <span>to</span>
                        <Input
                          type="time"
                          className="w-32"
                          value={availability?.weekly_schedule?.[day]?.end_time || '17:00'}
                          onChange={(e) => updateAvailability(day, 'end_time', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Impact Tab */}
        <TabsContent value="impact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-teal-600" />
                My Impact Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Impact Score */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-medium">Overall Impact Score</span>
                  <span className="font-bold text-teal-600">{impact?.impact_score || 0}/100</span>
                </div>
                <Progress value={impact?.impact_score || 0} className="h-3" />
              </div>

              {/* Activity Breakdown */}
              {impact?.activity_breakdown && Object.keys(impact.activity_breakdown).length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Hours by Activity</h4>
                  <div className="space-y-2">
                    {Object.entries(impact.activity_breakdown).map(([activity, hours]) => (
                      <div key={activity} className="flex justify-between items-center">
                        <span className="capitalize">{activity.replace(/_/g, ' ')}</span>
                        <Badge variant="outline">{hours} hours</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-teal-600">{impact?.total_shifts || 0}</p>
                  <p className="text-sm text-gray-600">Completed Shifts</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">{impact?.average_rating || 0}</p>
                  <p className="text-sm text-gray-600">Average Rating</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-teal-600" />
                Training Progress
              </CardTitle>
              <CardDescription>Complete required training to become fully certified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Compliance Status */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50">
                {training?.compliance_status === 'compliant' ? (
                  <>
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="font-medium text-green-700">Fully Compliant</p>
                      <p className="text-sm text-gray-600">All required training completed</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-6 w-6 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-700">Training Incomplete</p>
                      <p className="text-sm text-gray-600">Complete required courses below</p>
                    </div>
                  </>
                )}
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Training Progress</span>
                  <span className="text-sm font-medium">
                    {training?.hours_completed || 0} / {training?.hours_required || 16} hours
                  </span>
                </div>
                <Progress 
                  value={((training?.hours_completed || 0) / (training?.hours_required || 16)) * 100} 
                  className="h-2" 
                />
              </div>

              {/* Required Courses */}
              <div className="space-y-3">
                <h4 className="font-medium">Required Courses</h4>
                {training?.required_courses?.map((course) => {
                  const isCompleted = training?.completed_courses?.some(c => c.course_id === course.id);
                  return (
                    <div
                      key={course.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isCompleted ? 'bg-green-50 border-green-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <div className="h-5 w-5 border-2 rounded-full border-gray-300" />
                        )}
                        <div>
                          <p className="font-medium">{course.name}</p>
                          <p className="text-sm text-gray-500">{course.hours} hours</p>
                        </div>
                      </div>
                      {!isCompleted && (
                        <Button variant="outline" size="sm">
                          Start Course
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VolunteerEnhanced;
